import { useState } from 'react';
import { cn } from '../../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import { sendCustomEmail } from '../../../../services/admin/adminService';

// ── Custom Email Card ──
// Lets a super admin compose and send a fully custom one-off email (recipients,
// subject, raw HTML body + optional plain-text fallback). Not tied to restaurant data.

export function CustomEmailCard() {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [textBody, setTextBody] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const recipientList = recipients
    .split(/[,;\n]/)
    .map(r => r.trim())
    .filter(r => r.includes('@'));

  const canSend = recipientList.length > 0 && subject.trim() !== '' && htmlBody.trim() !== '' && textBody.trim() !== '' && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    const confirmed = window.confirm(
      `Send this custom email to ${recipientList.length} recipient(s)?\n\n` +
      `Subject: ${subject}\n` +
      `Recipients: ${recipientList.join(', ')}`
    );
    if (!confirmed) return;
    try {
      setSending(true);
      setResult(null);
      const response = await sendCustomEmail(recipientList, subject, htmlBody, textBody);
      setResult({ type: 'success', message: response.message });
    } catch (err: any) {
      setResult({ type: 'error', message: err.message || 'Failed to send custom email' });
    } finally {
      setSending(false);
    }
  };

  const inputClass = cn(
    'w-full px-3 py-2 rounded-lg border',
    'border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-700',
    primaryText, smallTextSize
  );

  return (
    <div className={cn(cardClass, 'p-6')}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className={cn(primaryText, semiBoldText)}>Custom Email</h3>
          <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
            Compose and send a one-off custom HTML email to any recipients
          </p>
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          className={cn(
            'px-4 py-2 rounded-lg transition-colors',
            'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
            smallTextSize, semiBoldText
          )}
        >
          {open ? 'Hide' : 'Compose Email'}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          <div>
            <label className={cn(secondaryText, smallTextSize, 'block mb-1')}>
              Recipients (comma, semicolon or newline separated)
            </label>
            <textarea
              placeholder="alice@example.com, bob@example.com"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              rows={2}
              className={inputClass}
            />
            {recipientList.length > 0 && (
              <p className={cn(secondaryText, 'text-xs mt-1')}>
                {recipientList.length} valid recipient(s)
              </p>
            )}
          </div>

          <div>
            <label className={cn(secondaryText, smallTextSize, 'block mb-1')}>
              Subject
            </label>
            <input
              type="text"
              placeholder="Subject line"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={cn(secondaryText, smallTextSize, 'block mb-1')}>
              HTML Body
            </label>
            <textarea
              placeholder="<html>...</html> — full HTML for the email body"
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              rows={12}
              className={cn(inputClass, 'font-mono')}
            />
            {htmlBody.trim() !== '' && (
              <button
                onClick={() => setShowPreview(p => !p)}
                className={cn(secondaryText, 'text-xs mt-1 underline')}
              >
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
            )}
          </div>

          {showPreview && htmlBody.trim() !== '' && (
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-white">
              <iframe
                title="Email preview"
                srcDoc={htmlBody}
                className="w-full h-96 bg-white dark:bg-white"
                sandbox=""
              />
            </div>
          )}

          <div>
            <label className={cn(secondaryText, smallTextSize, 'block mb-1')}>
              Plain-text fallback (required)
            </label>
            <textarea
              placeholder="Required. Shown by email clients that don't render HTML."
              value={textBody}
              onChange={(e) => setTextBody(e.target.value)}
              rows={4}
              className={inputClass}
            />
          </div>

          {result && (
            <div className={cn(
              'p-3 rounded-lg text-sm',
              result.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            )}>
              {result.message}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={cn(
                'px-4 py-2 rounded-lg transition-colors text-white dark:text-white',
                'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                smallTextSize, semiBoldText
              )}
            >
              {sending ? 'Sending...' : 'Send Custom Email'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
